import React from 'react';
import PropTypes from 'prop-types';

import {
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	IconButton
} from '@mui/material';

import TossIcon from './icons/TossIcon';
import Amount from './Amount';
import Quantity from './Quantity';

import useDarkMode from '../hooks/useDarkMode';
import useWidth from '../hooks/useWidth';

import {
	POSITIVE_AMOUNT_DARK_COLOR,
	POSITIVE_AMOUNT_LIGHT_COLOR,
	NEGATIVE_AMOUNT_COLOR
} from '../constants';

const Sparkline = ({ data, positive }) => {
	if (!data || data.length < 2) return null;
	const prices = data.map(d => d.price);
	const min = Math.min(...prices);
	const max = Math.max(...prices);
	const range = max - min || 1;
	const W = 64, H = 24;
	const points = prices.map((p, i) => {
		const x = (i / (prices.length - 1)) * W;
		const y = H - ((p - min) / range) * H;
		return `${x.toFixed(1)},${y.toFixed(1)}`;
	}).join(' ');
	const color = positive ? '#4caf50' : '#f44336';
	return (
		<svg width={W} height={H} style={{ display: 'block' }}>
			<polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
		</svg>
	);
};

const COLUMN_WIDTHS = {
	account: '16%', // 예시: Account 이름
	qty: '12%',     // 예시: 수량 (Qty)
	costBasis: '15%', // 예시: Cost Basis (가장 긴 금액을 고려)
	marketValue: '15%', // 예시: Market Value
	gainLossDiv: '21%', // 예시: Gain/Loss(Div) (가장 긴 금액 + 괄호 내용을 고려)
	return: '21%'  // 예시: Return(%)
};

export function InvestmentPerformance ({
	investment,
	performance,
	symbol,
	price,
	currency,
	weeklyPrices
}) {
	const isDarkMode = useDarkMode();
	const width = useWidth();
	const isSmallScreen = width === 'xs' || width === 'sm';
	const totalPerformance = performance.length > 0 ? performance.map(l => l.periodReturn).reduce((a, b) => a + b) : 0;
	const totalQuantity = performance.length > 0 ? performance.map(m => m.quantity).reduce((a, b) => a + b) : 0;
	const totalGain = performance.length > 0 ? performance.map(l => l.periodGain).reduce((a, b) => a + b) : 0;
	const totalDividend = performance.length > 0 ? performance.map(l => l.periodDiv).reduce((a, b) => a + b) : 0;
	const totalCostBasis = performance.length > 0 ? performance.map(m => m.costBasis).reduce((a, b) => a + b) : 0;
	const totalMarketValue = performance.length > 0 ? performance.map(l => l.marketValue).reduce((a, b) => a + b) : 0;
	const tossSymbol = symbol ? (symbol.split(':')[1] || symbol) : '';
	const handleTossClick = () => {
		if (tossSymbol) {
			window.open(`https://tossinvest.com/stocks/${tossSymbol}`, '_blank', 'noopener,noreferrer');
		}
	};

	return (
		<Paper sx={{ p: { xs: 0.5, sm: 1 }, mb: 1 }}>
			<Stack
				direction="row"
				alignItems="center"
				spacing={0.5}
				sx={(theme) => ({
					margin: theme.spacing(0.5)
				})}
			>
				<Typography variant="subtitle1">{investment}</Typography>
				<Sparkline data={weeklyPrices} positive={totalPerformance >= 0} />
				<Amount value={price} currency={currency} showSymbol showOriginal size="large" />
				{tossSymbol && (
					<IconButton onClick={handleTossClick} size="small" aria-label="open in toss">
						<TossIcon sx={{ width: '1.25rem', height: '1.25rem' }} />
					</IconButton>
				)}
			</Stack>
			{isSmallScreen ? (
				<Stack spacing={1}>
					{performance.map(i => (
						<Paper key={i.account} variant="outlined" sx={{ p: 1 }}>
							<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
								<Stack spacing={0.5}>
									<Typography variant="body1" sx={{ fontWeight: 'bold' }}>{i.account}</Typography>
									<Amount value={i.marketValue} currency={currency} showSymbol size="large" />
									<Stack direction="row" alignItems="center">
										<Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Buy:</Typography>
										<Amount value={i.costBasis} currency={currency} showSymbol />
										<Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>|</Typography>
										<Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Qty:</Typography>
										<Quantity value={i.quantity}/>
									</Stack>
								</Stack>
								<Stack spacing={0.5} alignItems="flex-end">
									<Typography variant="body1" sx={{ color: i.periodReturn > 0 ? (isDarkMode ? POSITIVE_AMOUNT_DARK_COLOR : POSITIVE_AMOUNT_LIGHT_COLOR) : NEGATIVE_AMOUNT_COLOR }}>
										{(i.costBasis !== 0 ? (i.periodReturn / i.costBasis * 100) : 0).toFixed(2)}%
									</Typography>
									<Amount value={i.periodReturn} currency={currency} showSymbol negativeColor />
									<Stack direction="row" alignItems="center">
										<Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Gain:</Typography>
										<Amount value={i.periodGain} currency={currency} showSymbol negativeColor />
									</Stack>
									<Stack direction="row" alignItems="center">
										<Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Div:</Typography>
										<Amount value={i.periodDiv} currency={currency} showSymbol negativeColor />
									</Stack>									
								</Stack>
								
							</Stack>
						</Paper>
					))}
					<Paper variant="outlined" sx={{ p: 1, bgcolor: 'action.hover' }}>
						<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
							<Stack spacing={0.5}>
								<Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total</Typography>
								<Amount value={totalMarketValue} currency={currency} showSymbol size="large" />
								<Stack direction="row" alignItems="center">
									<Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Buy:</Typography>
									<Amount value={totalCostBasis} currency={currency} showSymbol />
									<Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>|</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Qty:</Typography>
									<Quantity value={totalQuantity}/>
								</Stack>
							</Stack>
							<Stack spacing={0.5} alignItems="flex-end">
								<Typography variant="body1" sx={{ color: totalPerformance > 0 ? (isDarkMode ? POSITIVE_AMOUNT_DARK_COLOR : POSITIVE_AMOUNT_LIGHT_COLOR) : NEGATIVE_AMOUNT_COLOR }}>
									{(totalCostBasis !== 0 ? (totalPerformance / totalCostBasis * 100) : 0).toFixed(2)}%
								</Typography>
								<Amount value={totalPerformance} currency={currency} showSymbol negativeColor />
								<Stack direction="row" alignItems="center">
									<Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Gain:</Typography>
									<Amount value={totalGain} currency={currency} showSymbol negativeColor />
								</Stack>
								<Stack direction="row" alignItems="center">
									<Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Div:</Typography>
									<Amount value={totalDividend} currency={currency} showSymbol negativeColor />
								</Stack>
							</Stack>
						</Stack>
					</Paper>
				</Stack>
			) : (
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell align="left" sx={{ width: COLUMN_WIDTHS.account }}>
									<Typography variant="body2">Account</Typography>
								</TableCell>
								<TableCell align="right" sx={{ width: COLUMN_WIDTHS.qty }}>
									<Typography variant="body2">Qty</Typography>
								</TableCell>
								<TableCell align="right" sx={{ width: COLUMN_WIDTHS.costBasis }}>
									<Typography variant="body2">Cost Basis</Typography>
								</TableCell>
								<TableCell align="right" sx={{ width: COLUMN_WIDTHS.marketValue }}>
									<Typography variant="body2">Market Value</Typography>
								</TableCell>
								<TableCell align="right" sx={{ width: COLUMN_WIDTHS.gainLossDiv }}>
									<Typography variant="body2">Gain/Loss(Div)</Typography>
								</TableCell>
								<TableCell align="right" sx={{ width: COLUMN_WIDTHS.return }}>
									<Stack direction="row" justifyContent="flex-end">
										<Typography variant="body2">Return</Typography>
										<Typography variant="body2">(%)</Typography>
									</Stack>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{performance.map(i => (
								<TableRow key={i.account}>
									<TableCell align="left" sx={{ width: COLUMN_WIDTHS.account }}>
										<Typography variant="body2">{i.account}</Typography>
									</TableCell>
									<TableCell align="right" sx={{ width: COLUMN_WIDTHS.qty }}>
										<Quantity value={i.quantity}/>
									</TableCell>
									<TableCell align="right" sx={{ width: COLUMN_WIDTHS.costBasis }}>
										<Amount value={i.costBasis} currency={currency} showSymbol />
									</TableCell>								
									<TableCell align="right" sx={{ width: COLUMN_WIDTHS.marketValue }}>
										<Amount value={i.marketValue} currency={currency} showSymbol />
									</TableCell>
									<TableCell align="right" sx={{ width: COLUMN_WIDTHS.gainLossDiv }}>
										<Stack direction="row" justifyContent="flex-end">
											<Amount value={i.periodGain} currency={currency} showSymbol negativeColor />
											(<Amount value={i.periodDiv} currency={currency} showSymbol negativeColor />)
										</Stack>
									</TableCell>
									<TableCell align="right" sx={{ width: COLUMN_WIDTHS.return }}>
										<Stack direction="row" justifyContent="flex-end">
											<Amount value={i.periodReturn} currency={currency} showSymbol negativeColor />
											<Typography variant="caption" sx={{ color: i.periodReturn > 0 ? (isDarkMode ? POSITIVE_AMOUNT_DARK_COLOR : POSITIVE_AMOUNT_LIGHT_COLOR) : NEGATIVE_AMOUNT_COLOR }}>
												({(i.costBasis !== 0 ? (i.periodReturn / i.costBasis * 100) : 0).toFixed(2)}%)
											</Typography>
										</Stack>
									</TableCell>
								</TableRow>
							))}
							<TableRow sx={{ '& > *': { borderTop: '2px solid rgba(224, 224, 224, 1)', fontWeight: 'bold' } }}>
								<TableCell align="left" sx={{ width: COLUMN_WIDTHS.account }}>
									<Typography variant="body2">Total</Typography>
								</TableCell>
								<TableCell align="right" sx={{ width: COLUMN_WIDTHS.qty }}>
									<Quantity value={totalQuantity}/>
								</TableCell>
								<TableCell align="right" sx={{ width: COLUMN_WIDTHS.costBasis }}>
									<Amount value={totalCostBasis} currency={currency} showSymbol />
								</TableCell>
								<TableCell align="right" sx={{ width: COLUMN_WIDTHS.marketValue }}>
									<Amount value={totalMarketValue} currency={currency} showSymbol />
								</TableCell>
								<TableCell align="right" sx={{ width: COLUMN_WIDTHS.gainLossDiv }}>
									<Stack direction="row" justifyContent="flex-end">
										<Amount value={totalGain} currency={currency} showSymbol negativeColor />
										(<Amount value={totalDividend} currency={currency} showSymbol negativeColor />)
									</Stack>
								</TableCell>
								<TableCell align="right" sx={{ width: COLUMN_WIDTHS.return }}>
									<Stack direction="row" justifyContent="flex-end">
										<Amount value={totalPerformance} currency={currency} showSymbol negativeColor />
										<Typography variant="caption" sx={{ color: totalPerformance > 0 ? (isDarkMode ? POSITIVE_AMOUNT_DARK_COLOR : POSITIVE_AMOUNT_LIGHT_COLOR) : NEGATIVE_AMOUNT_COLOR }}>
											({(totalCostBasis !== 0 ? (totalPerformance / totalCostBasis * 100) : 0).toFixed(2)}%)
										</Typography>
									</Stack>
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Paper>
	);
}

InvestmentPerformance.propTypes = {
	investment: PropTypes.string.isRequired,
	performance: PropTypes.array.isRequired,
	currency: PropTypes.string,
	price: PropTypes.number,
	symbol: PropTypes.string
};

export default InvestmentPerformance;
